import Pagination from "app/components/shared/Pagination";
import usePagination, {
  PaginationProps,
} from "app/components/analytics/shared/hooks/usePagination";
import { LoadingPanels } from "app/components/analytics/shared/LoadingStates";
import EmptyState from "app/components/analytics/shared/EmptyState";
import ErrorState from "app/components/analytics/shared/ErrorState";
import Row, { Props as RowProps } from "./Row";
import { Container } from "../../shared/FeedbackMessage";
import Icon from "app/components/shared/Icon";

const Issues = ({
  endpoint,
  flakyTestAssignmentFeatureAvailable,
}: {
  endpoint: string;
  flakyTestAssignmentFeatureAvailable: boolean;
}) => {
  const {
    currentData,
    metadata,
    pageCount,
    handleOnChange,
    loading,
    error,
  }: PaginationProps = usePagination({ endpoint });

  if (error) {
    return (
      <ErrorState
        emoji="🕳️"
        heading="There was an error loading the test executions"
        subheading="Try refreshing the page; if the problem persists"
        urlText="let us know"
        url="mailto:support@buildkite.com"
      />
    );
  }

  if (!loading && currentData.length === 0) {
    return (
      <EmptyState
        emoji="🪴"
        heading="No issues found in this run"
        subheading="Your day is already looking up."
        loadingRows={true}
      />
    );
  }

  const {
    number_of_executions,
    number_of_executions_displayed,
    number_of_issues,
    threshold,
    edit_threshold_url,
  } = metadata || {};

  return (
    <>
      {loading ? (
        <LoadingPanels rows={10} columns={1} />
      ) : (
        currentData.map((test: RowProps["test"]) => (
          <Row
            key={test.execution_id}
            test={test}
            flakyTestAssignmentFeatureAvailable={
              flakyTestAssignmentFeatureAvailable
            }
          />
        ))
      )}

      <Pagination pageCount={pageCount} onPageChange={handleOnChange} />

      {number_of_executions &&
        number_of_executions_displayed &&
        number_of_issues && (
          <Container>
            <Icon icon="info-circle" />
            <div>
              <p className="mt0">
                This list shows {number_of_executions_displayed} test
                {number_of_executions_displayed > 1 && "s"}, ordered by
                flakiest, slowest and failed, out of a total of{" "}
                {number_of_executions} test
                {number_of_executions_displayed > 1 && "s"} with{" "}
                {number_of_issues} issue{number_of_issues > 1 && "s"}. A run is
                flagged as slow when the slowest 1% of tests take more than{" "}
                {threshold}% of the total run time —{" "}
                <a
                  href={edit_threshold_url}
                  className="hover-underline bold"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  change this metric
                </a>
                .
              </p>
            </div>
          </Container>
        )}
    </>
  );
};

export default Issues;
